/**
 * Hosted project state for the Roblox platform private alpha.
 *
 * The hosted Worker owns the project records, so this store is a thin cache of
 * what the last request returned plus the currently open project. It is
 * separate from T3's environment-backed project entities on purpose: hosted
 * projects have no workspace root and no local environment behind them.
 *
 * Four surfaces read this list at once (workspace, sidebar, Add Project dialog
 * host, memory settings) and each of them asks for a refresh when it mounts, so
 * concurrent list requests are the normal case rather than an edge case. That
 * makes ordering the store's problem, not any single hook's: a per-hook "is a
 * request in flight" ref cannot see another hook's request, so a list that was
 * issued before a project was created could still land afterwards and drop the
 * new project along with its selection. Both halves of the fix live here —
 * request tokens so an older answer can never overwrite a newer one, and a
 * merge that keeps a locally created project until a list that could actually
 * have seen it says otherwise.
 */
import { create } from "zustand";

import type { HostedProject } from "./api";

export type HostedProjectLoadStatus = "idle" | "loading" | "ready" | "error";

/**
 * A project this browser created that no authoritative list has confirmed yet.
 * `afterToken` is the newest load that had already started when the project was
 * created, so any response up to and including that token predates the create
 * and cannot be trusted to contain it.
 */
interface PendingHostedProjectCreation {
  readonly project: HostedProject;
  readonly afterToken: number;
}

export interface HostedProjectStoreState {
  readonly projects: ReadonlyArray<HostedProject>;
  readonly selectedProjectId: string | null;
  readonly status: HostedProjectLoadStatus;
  readonly errorMessage: string | null;
  /** Newest load token handed out by `beginLoad`. */
  readonly loadToken: number;
  /** Newest load token whose answer has been applied. */
  readonly appliedToken: number;
  readonly pendingCreations: ReadonlyArray<PendingHostedProjectCreation>;
  /**
   * Bumped whenever the store is cleared for a new Clerk account. Mounted
   * surfaces watch this so an account switch reloads instead of leaving them
   * showing an empty list they never asked for.
   */
  readonly accountEpoch: number;
}

interface HostedProjectStore extends HostedProjectStoreState {
  /** Marks a load as started and returns the token its answer must carry. */
  readonly beginLoad: () => number;
  readonly applyProjects: (projects: ReadonlyArray<HostedProject>, token: number) => void;
  readonly applyError: (message: string, token: number) => void;
  /** Adds or replaces one project and selects it, used right after creation. */
  readonly upsertAndSelectProject: (project: HostedProject) => void;
  readonly selectProject: (projectId: string | null) => void;
  readonly reset: () => void;
}

const EMPTY_PROJECTS: ReadonlyArray<HostedProject> = [];
const EMPTY_PENDING: ReadonlyArray<PendingHostedProjectCreation> = [];

const INITIAL_STATE: HostedProjectStoreState = {
  projects: EMPTY_PROJECTS,
  selectedProjectId: null,
  status: "idle",
  errorMessage: null,
  loadToken: 0,
  appliedToken: 0,
  pendingCreations: EMPTY_PENDING,
  accountEpoch: 0,
};

/**
 * Shared in-flight list request. Every hosted surface asks for the project list
 * when it mounts, and they all want the same answer, so the first caller's
 * request is the one that runs and the rest await it.
 */
let inFlightLoad: Promise<HostedProjectsLoadOutcome> | null = null;

/**
 * Clerk account the cached projects belong to. Kept outside the store because it
 * only ever gates a reset: a component subscribing to it would re-render for
 * something it never displays.
 */
let cachedProjectsAccountId: string | null = null;

function resolveSelection(
  projects: ReadonlyArray<HostedProject>,
  selectedProjectId: string | null,
): string | null {
  if (selectedProjectId && projects.some((project) => project.id === selectedProjectId)) {
    return selectedProjectId;
  }
  return projects[0]?.id ?? null;
}

export const useHostedProjectStore = create<HostedProjectStore>((set) => ({
  ...INITIAL_STATE,
  beginLoad: () => {
    let token = 0;
    set((state) => {
      token = state.loadToken + 1;
      return {
        loadToken: token,
        // A refresh behind an already-loaded list is a background update, not a
        // reason to blank the surface that is showing it.
        status: state.status === "ready" ? "ready" : "loading",
        errorMessage: null,
      };
    });
    return token;
  },
  applyProjects: (projects, token) =>
    set((state) => {
      if (token <= state.appliedToken) {
        // A newer answer already landed. Applying this one would move the list
        // backwards, which is the reset-the-selection bug in its general form.
        return state;
      }

      const listedIds = new Set(projects.map((project) => project.id));
      const pendingCreations = state.pendingCreations.filter(
        (pending) => !listedIds.has(pending.project.id) && token <= pending.afterToken,
      );
      const merged =
        pendingCreations.length === 0
          ? projects
          : [...pendingCreations.map((pending) => pending.project), ...projects];

      return {
        projects: merged,
        pendingCreations: pendingCreations.length === 0 ? EMPTY_PENDING : pendingCreations,
        status: "ready",
        errorMessage: null,
        appliedToken: token,
        selectedProjectId: resolveSelection(merged, state.selectedProjectId),
      };
    }),
  applyError: (message, token) =>
    set((state) => {
      if (token <= state.appliedToken) {
        return state;
      }
      // The cached list stays: a failed refresh should surface an error without
      // emptying a surface that was working a moment ago.
      return { status: "error", errorMessage: message, appliedToken: token };
    }),
  upsertAndSelectProject: (project) =>
    set((state) => ({
      projects: [project, ...state.projects.filter((candidate) => candidate.id !== project.id)],
      pendingCreations: [
        { project, afterToken: state.loadToken },
        ...state.pendingCreations.filter((pending) => pending.project.id !== project.id),
      ],
      selectedProjectId: project.id,
      status: "ready",
      errorMessage: null,
    })),
  selectProject: (projectId) => set({ selectedProjectId: projectId }),
  reset: () =>
    set((state) => {
      inFlightLoad = null;
      // The cleared cache belongs to nobody, so the next account sync is a real
      // change and reloads rather than short-circuiting.
      cachedProjectsAccountId = null;
      return {
        ...INITIAL_STATE,
        // Tokens keep counting across a reset. A list that is still in flight
        // carries a token at or below the new `appliedToken`, so it is dropped
        // rather than landing in the fresh store — which for an account switch
        // would mean showing the previous account's projects.
        loadToken: state.loadToken,
        appliedToken: state.loadToken,
        accountEpoch: state.accountEpoch + 1,
      };
    }),
}));

export type HostedProjectsLoadOutcome =
  | { readonly ok: true }
  | { readonly ok: false; readonly error: unknown };

/**
 * Points the project cache at the signed-in account, clearing it whenever the
 * account changes.
 *
 * Projects are per-account, so a switch (or a sign-out) makes every cached
 * record and every pending creation wrong. Without this the next account sees
 * the previous one's sidebar until its own list lands, and a hosted 403 for a
 * non-owner would leave real project names from the owner's account on screen.
 */
export function syncHostedProjectsAccount(accountId: string | null): void {
  if (cachedProjectsAccountId === accountId) {
    return;
  }
  useHostedProjectStore.getState().reset();
  cachedProjectsAccountId = accountId;
}

/**
 * Subscribe to "the signed-in account changed".
 *
 * Hosted surfaces that keep their own copy of account-scoped data (memory
 * documents, provider status) have to discard and refetch it on a switch, and a
 * direct switch never unmounts them, so a mount effect is not enough. Reading
 * this rather than each tracking a Clerk user id keeps one definition of when
 * hosted state goes stale.
 */
export function useHostedAccountEpoch(): number {
  return useHostedProjectStore((state) => state.accountEpoch);
}

/**
 * Runs one project list request through the store's token discipline, sharing a
 * single request between every caller that asks while it is in flight. Callers
 * still get the outcome so each can record its own authorization result.
 *
 * `describeError` is passed in rather than imported so this module stays free of
 * React and Clerk.
 */
export function loadHostedProjects(options: {
  readonly list: () => Promise<ReadonlyArray<HostedProject>>;
  readonly describeError: (error: unknown) => string;
}): Promise<HostedProjectsLoadOutcome> {
  if (inFlightLoad) {
    return inFlightLoad;
  }

  const store = useHostedProjectStore.getState();
  const token = store.beginLoad();
  const run = (async (): Promise<HostedProjectsLoadOutcome> => {
    try {
      const listed = await options.list();
      useHostedProjectStore.getState().applyProjects(listed, token);
      return { ok: true };
    } catch (error) {
      useHostedProjectStore.getState().applyError(options.describeError(error), token);
      return { ok: false, error };
    } finally {
      inFlightLoad = null;
    }
  })();
  inFlightLoad = run;
  return run;
}

export function selectHostedProject(
  state: HostedProjectStoreState,
  projectId: string | null,
): HostedProject | null {
  if (!projectId) {
    return null;
  }
  return state.projects.find((project) => project.id === projectId) ?? null;
}

export function selectActiveHostedProject(state: HostedProjectStoreState): HostedProject | null {
  return selectHostedProject(state, state.selectedProjectId);
}
